import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// Fix duplicate custom element registration issues (must be before any component imports)
import App from './App';
import './index.css';
import './lib/fix-custom-elements';
// Initialize auth system to avoid circular dependency issues
import './lib/auth/init-auth-system';
import { networkDiagnostics } from './lib/utils/network-diagnostics';
// Sentry initialization happens automatically in the wrapper
import './lib/sentry-wrapper';
import { initBrowserAnalytics } from '@/lib/privacy/analytics-loader';

// Sentry initialization is handled in sentry-wrapper.ts to avoid ESM/CommonJS conflicts
initBrowserAnalytics();

const rootElement = document.getElementById('root')!;
const root = createRoot(rootElement);

if (import.meta.env.VITE_THEME_PREVIEW === 'true') {
  try { document.documentElement.classList.add('theme-preview'); } catch {}
}

// Apply the warm semantic-alias rollout only when explicitly enabled
if (import.meta.env.VITE_THEME_WARM === 'true') {
  try { document.documentElement.classList.add('theme-warm'); } catch {}
}

// Only use StrictMode in production to avoid double-mounting issues in development
if (import.meta.env.PROD) {
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
} else {
  root.render(<App />);
}

if (typeof window !== 'undefined') {
  (window as any).debugAuth = async () => {
    const { unifiedAuthService } = await import('@/lib/auth/unified-auth-service');
    const session = unifiedAuthService.getSession();
    const user = unifiedAuthService.getUser();
    const profile = unifiedAuthService.getUserProfile();
    const isLoading = unifiedAuthService.isLoading();
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    console.log('=== Auth Debug Info ===');
    console.log('Is Mobile:', isMobile);
    console.log('User Agent:', navigator.userAgent);
    console.log('Has Session:', !!session);
    console.log('Has User:', !!user);
    console.log('Has Profile:', !!profile);
    console.log('Is Loading:', isLoading);
    console.log('User Email:', user?.email);
    console.log('Profile Role:', profile?.role);
    
    return {
      isMobile,
      hasSession: !!session,
      hasUser: !!user,
      hasProfile: !!profile,
      isLoading,
      userEmail: user?.email,
      profileRole: profile?.role
    };
  };
  
  (window as any).diagnoseNetwork = () => networkDiagnostics.diagnoseAndLog();
  
  const checkConnection = async () => {
    const lastCheck = localStorage.getItem('last-network-check');
    const now = Date.now();
    
    // Only auto-check once per hour
    if (!lastCheck || now - parseInt(lastCheck) > 3600000) {
      const result = await networkDiagnostics.testSupabaseConnection();
      if (!result.reachable) {
        console.error('Supabase connection issue detected on startup');
        networkDiagnostics.diagnoseAndLog();
      }
      localStorage.setItem('last-network-check', now.toString());
    }
  };
  
  // Check connection after a short delay to not block app startup
  setTimeout(checkConnection, 2000);
  
}
